import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Box } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { InfoBox } from '@resurs-internal/mui';
import { useEffect, useState } from 'react';

import EmailField from '../../components/EmailField/EmailField';
import GovernmentIdField from '../../components/GovernmentIdField/GovernmentIdField';
import Group from '../../components/Group/Group';
import IconWithText from '../../components/IconWithText/IconWithText';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import PhoneField from '../../components/PhoneField/PhoneField';
import RadioField from '../../components/RadioField/RadioField';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { RootState } from '../../store';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { WizardPageAction } from '../../store/wizard/wizard';

export const selectAllCoApplicantFieldStates: (state: RootState) => () => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state: RootState) => (): IFieldState[] =>
        state.form.formFieldStates.filter((fieldState) => fieldState.fieldName.startsWith('coapplicant')),
);

/**
 * A hook that validates that the co applicants government id is not
 * the same as the main applicants.
 */
const useGovernmentIdUniqueValidation = () => {
    const dispatch = useAppDispatch();
    const t = useTranslation();
    const coapplicantGovernmentId = useAppSelector(selectFieldStateByName)('coapplicantGovernmentId');
    const applicantGovernmentId = useAppSelector(selectFieldStateByName)('applicantGovernmentId');

    useEffect(() => {
        if (coapplicantGovernmentId && coapplicantGovernmentId.value === applicantGovernmentId?.value) {
            dispatch(
                addOrUpdateFormField({
                    ...coapplicantGovernmentId,
                    hasError: true,
                    errorMessage: t('validation.governmentid.duplicate'),
                }),
            );
        }
    }, [coapplicantGovernmentId?.value, applicantGovernmentId]);
};

const CoApplicantPage = () => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    useGovernmentIdUniqueValidation();

    const { totalSections, totalAboutTheLoanPages } = usePagesConfig();
    const { countryCode } = useSettings();
    const { currentPage } = usePagesContext();
    useSetBannerType('bannerTwo');
    const coApplicantAnswerFieldStates = useAppSelector(selectAllCoApplicantFieldStates)();
    const coapplicantToggle = useAppSelector(selectFieldStateByName)('coapplicantToggle');
    const defaultPagesAction = useDefaultPagesAction();
    const [requiredFields, setRequiredFields] = useState([]);
    const validationErrors = useValidation(requiredFields);
    const currentPageInSection = countryCode === 'DK' ? 1 : 0;

    useEffect(() => {
        const fields: any = [];

        if (coapplicantToggle?.value === 'true') {
            fields.push('coapplicantGovernmentId');
            fields.push('coapplicantEmailAddress');
            fields.push('coapplicantMobileNumber');
        }
        fields.push('coapplicantToggle');
        setRequiredFields(fields);
    }, [coapplicantToggle]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next') {
            if (validationErrors.length > 0) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
        }

        return defaultPagesAction(action);
    };

    useEffect(() => {
        const fields = coApplicantAnswerFieldStates.filter((field) => field.fieldName !== 'coapplicantToggle');
        if (coapplicantToggle?.value === 'false') {
            fields
                .flatMap((field) => field.fieldName)
                .forEach((field) => {
                    dispatch(addOrUpdateFormField({ fieldName: field, value: '' }));
                });
        }
    }, [coapplicantToggle]);

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={currentPageInSection}
                sectionTitle={translate('field.section.about-the-loan')}
            />
            <Title text={translate('page.co-applicant.title')} />
            <IconWithText icon={faCheck} text="field.co-applicant.description.text.one" />
            <RadioField
                name={'coapplicantToggle'}
                options={[
                    { id: 'true', label: translate('field.co-applicant-toggle.option.yes.label') },
                    { id: 'false', label: translate('field.co-applicant-toggle.option.no.label') },
                ]}
            />
            {coapplicantToggle?.value === 'true' && (
                <>
                    <Group>
                        <GovernmentIdField
                            name={'coapplicantGovernmentId'}
                            label={translate('field.co-applicant-government-id.label')}
                            countrycode={countryCode}
                        />
                        <EmailField
                            name={'coapplicantEmailAddress'}
                            label={translate('field.co-applicant-email.label')}
                        />
                        <PhoneField
                            name={'coapplicantMobileNumber'}
                            label={translate('field.co-applicant-mobile-number.label')}
                        />
                    </Group>
                    <Box mt={2}>
                        <InfoBox variant="no-title">{translate('field.co-applicant.additional-info')}</InfoBox>
                    </Box>
                </>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={currentPageInSection}
                sectionTitle={translate('field.section.about-the-loan')}
            />
        </>
    );
};

export default CoApplicantPage;
